// Adapted from cartadigital-pos app/composables/useMoneyDisplay.ts.
// Centralizes POS V2 money display and ensures tax is never double-applied:
// - Catalog money (menu prices): tax applied with partial=true (SilentAdd/AddAndShow).
// - Computed money (cart lines/totals): already tax-included, only round+format.
import { TaxDisplayMode } from '@/enums/TaxDisplayMode';
import {
  computeCatalogDisplayAmount,
  computeVariantLabelParts,
} from '@/domain/pos/pricing/display/money-display';
import { displayPrice, roundPrice } from '@/utils';
import { useRestaurantStore } from '@/stores/restaurant.store';

export interface IFormatVariantLabelInput {
  optionMain: boolean;
  optionPrice: number;
  baseItemPrice: number;
}

export function usePosMoney() {
  const restaurantStore = useRestaurantStore();

  function formatCurrency(amount: number): string {
    const symbol = restaurantStore.currency?.symbol ?? '$';
    return `${symbol} ${displayPrice(amount)}`;
  }

  function getTaxContext() {
    return {
      taxPercent: restaurantStore.restaurant?.tax ?? null,
      taxMode:
        restaurantStore.restaurantSettings?.taxMode ?? TaxDisplayMode.NoTax,
    };
  }

  function formatCatalogMoney(amount: number): string {
    const { taxPercent, taxMode } = getTaxContext();
    return formatCurrency(
      computeCatalogDisplayAmount({ amount, taxPercent, taxMode })
    );
  }

  function formatComputedMoney(amount: number): string {
    return formatCurrency(roundPrice(amount));
  }

  function formatVariantLabel(input: IFormatVariantLabelInput): string | null {
    const { taxPercent, taxMode } = getTaxContext();

    const parts = computeVariantLabelParts({
      optionMain: input.optionMain,
      variantsFullPrice: !!restaurantStore.restaurantSettings
        ?.variantsFullPrice,
      optionPrice: input.optionPrice,
      baseItemPrice: input.baseItemPrice,
      taxPercent,
      taxMode,
    });

    if (!parts.show) return null;

    const formatted = formatCurrency(parts.amount);
    return parts.sign ? `${parts.sign} ${formatted}` : formatted;
  }

  return {
    formatCurrency,
    formatCatalogMoney,
    formatComputedMoney,
    formatVariantLabel,
  };
}
